//@ts-nocheck
import { serviceTypes } from './serviceTypes/serviceTypes';
import { isSameDay, isAfter, isBefore, startOfDay } from 'date-fns';

const defaultCode = 'MISC';

/**
 * Retrieves the icon for the given service type code.
 *
 * @param {string} code - The service type code
 * @return {JSX.Element} The icon for the service type
 */
export const getServiceTypeIcon = (code) => {
  const serviceType =
    serviceTypes.find((st) => st.code === code) ||
    serviceTypes.find((st) => st.code === defaultCode);

  return serviceType?.icon;
};

/**
 * Returns the count of each service type in the given data array.
 *
 * @param {Array} data - The array of objects containing service type information.
 * @return {Array} An array of objects with service type codes, ids, and counts.
 */
export const countServiceTypes = (data = []) => {
  const counts = data.reduce((acc, item) => {
    const {
      servicetypecode: code,
      servicetypeid: id,
      servicetypename: description
    } = item.servicetype || {};
    acc[code] = { id, code, description, count: (acc[code]?.count || 0) + 1 };
    return acc;
  }, {});

  return Object.values(counts);
};

/**
 * Retrieves the value of the service type field with the given key from the given service.
 *
 * @param {Object} service - The service object containing service type information.
 * @param {string} fieldKey - The key of the service type field to retrieve the value for.
 * @return {string|null} The value of the service type field, or null if no matching field is found.
 */
export const getServiceFieldValue = (service, fieldKey) => {
  if (!service?.servicetype?.servicetypefield || !service?.servicefields)
    return null;

  // Find the matching service type field definition
  const fieldDef = service.servicetype.servicetypefield.find((stf) => {
    const key = stf?.key || stf?.servicefield?.key;
    return key === fieldKey;
  });

  if (!fieldDef) return null;

  const fieldId =
    fieldDef?.servicefieldid || fieldDef?.servicefield?.servicefieldid;
  if (!fieldId) return null;

  const fieldData = service.servicefields.find(
    (sf) => sf.servicefieldid === fieldId
  );

  return fieldData?.options?.[0]?.value ?? null;
};

/**
 * Retrieves accommodation details from the given service object.
 *
 * The accommodation details include the check-in date, check-out date, and
 * duration of stay. The check-in date is based on the day the service is on,
 * and the check-out date is calculated by adding the number of nights.
 *
 * @param {Object} service - The service object containing accommodation details.
 * @param {Date} serviceDayDate - The date of the day this service is on.
 * @return {Object} An object with the following properties:
 *   - `checkInDate`: The calculated check-in date as a Date object.
 *   - `checkOutDate`: The calculated check-out date as a Date object.
 *   - `duration`: The duration of stay in days (number of nights).
 */
export const getAccommodationDetails = (service, serviceDayDate = null) => {
  const numberOfNights = getServiceFieldValue(service, 'NUMBEROFNIGHTS');

  let duration = 1; // Default duration
  let checkInDate = null;
  let checkOutDate = null;

  // Parse number of nights
  if (numberOfNights && !isNaN(parseInt(numberOfNights))) {
    duration = parseInt(numberOfNights);
  }

  // Calculate dates based on service day and number of nights
  if (serviceDayDate) {
    checkInDate = startOfDay(serviceDayDate);
    checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkOutDate.getDate() + duration);
  }

  return {
    checkInDate,
    checkOutDate,
    duration: duration > 0 ? duration : 1
  };
};

/**
 * Decides whether to show the accommodation icon on a given day.
 *
 * The accommodation check-in date is the day the accommodation service is on,
 * and the check-out date is calculated by adding the number of nights.
 * The icon is shown if the current day is within the check-in to check-out
 * range (excluding check-out date).
 *
 * @param {Object} accommodation - The accommodation object with service fields.
 * @param {Date} currentDayDate - The date of the current day.
 * @param {Date} accommodationDayDate - The date of the day the accommodation service is on.
 * @param {number} accommodationDayNumber - The day number of the accommodation.
 * @param {number} currentDayNumber - The day number of the current day.
 * @returns {boolean} Whether to show the accommodation icon on the given day.
 */
export const shouldShowAccommodationIcon = (
  accommodation,
  currentDayDate,
  accommodationDayDate,
  accommodationDayNumber,
  currentDayNumber
) => {
  // Get accommodation details with calculated dates
  const { checkInDate, checkOutDate, duration } = getAccommodationDetails(
    accommodation,
    accommodationDayDate
  );

  // If we have calculated check-in and check-out dates, use them
  if (checkInDate && checkOutDate && currentDayDate) {
    // Normalize all dates to start of day to avoid timezone issues
    const normalizedCurrentDay = startOfDay(currentDayDate);
    const normalizedCheckIn = startOfDay(checkInDate);
    const normalizedCheckOut = startOfDay(checkOutDate);

    // Show icon if day is within check-in to check-out range (EXCLUDING check-out date)
    const isOnOrAfterCheckIn =
      isSameDay(normalizedCurrentDay, normalizedCheckIn) ||
      isAfter(normalizedCurrentDay, normalizedCheckIn);

    const isBeforeCheckOut = isBefore(normalizedCurrentDay, normalizedCheckOut);

    return isOnOrAfterCheckIn && isBeforeCheckOut;
  }

  // Fallback to duration-based logic using day numbers
  const daysDifference = currentDayNumber - accommodationDayNumber;
  return daysDifference >= 0 && daysDifference < duration;
};

/**
 * Process the services for the given day by adding accommodation icons
 * that appear on this day (but are not part of the current day's services).
 *
 * This function takes all the detailed days, the current day, and the
 * current day's date as input. It returns an array of processed services
 * that contains the accommodation icons that should appear first,
 * followed by the services of the current day in their original order.
 *
 * The accommodation icons are filtered based on the accommodation's
 * calculated check-in and check-out dates (based on number of nights),
 * and are added to the processed services array with an additional
 * `isAccommodationExtension` property set to true.
 *
 * @param {Array<Object>} allDetailedDays - The array of all detailed days.
 * @param {Object} currentDay - The current day object.
 * @param {Date} currentDayDate - The date of the current day.
 * @return {Array<Object>} The processed services array.
 */
export const processServicesForDay = (
  allDetailedDays,
  currentDay,
  currentDayDate
) => {
  const currentDayServices = currentDay.tripdetaileddayservice || [];
  const processedServices = [];

  // First, add accommodation icons that should appear first (from other days)
  allDetailedDays.forEach((day) => {
    const dayAccommodations = (day.tripdetaileddayservice || []).filter(
      (service) => service.servicetype?.code === 'ACCO'
    );

    dayAccommodations.forEach((accommodation) => {
      // Only add if this accommodation is NOT in the current day's services
      const isInCurrentDay = currentDayServices.some(
        (service) =>
          service.tripdetaileddayserviceid ===
          accommodation.tripdetaileddayserviceid
      );

      if (
        !isInCurrentDay &&
        shouldShowAccommodationIcon(
          accommodation,
          currentDayDate,
          day?.date ? new Date(day.date) : null, // Pass the accommodation day date
          day?.daynumber,
          currentDay?.daynumber
        )
      ) {
        processedServices.push({
          icon: accommodation.servicetype.iconref,
          servicetype: accommodation.servicetype,
          isAccommodationExtension: true,
          originalDayNumber: day.daynumber,
          calculatedCheckIn: getAccommodationDetails(
            accommodation,
            day?.date ? new Date(day.date) : null
          ).checkInDate,
          calculatedCheckOut: getAccommodationDetails(
            accommodation,
            day?.date ? new Date(day.date) : null
          ).checkOutDate
        });
      }
    });
  });

  // Then add current day's services in their original order
  currentDayServices.forEach((service) => {
    processedServices.push({
      icon: service.servicetype?.iconref,
      servicetype: service.servicetype,
      isAccommodationExtension: false,
      tripdetaileddayserviceid: service.tripdetaileddayserviceid,
      // Add calculated dates for accommodations in current day too
      ...(service.servicetype?.code === 'ACCO' && {
        calculatedCheckIn: getAccommodationDetails(service, currentDayDate)
          .checkInDate,
        calculatedCheckOut: getAccommodationDetails(service, currentDayDate)
          .checkOutDate
      })
    });
  });

  return processedServices;
};
